import type { ClassSessionUpdateInput } from "@/types/class_session"
import { memo, useEffect, useId, useMemo } from "react"

import { type SubmitHandler, useForm } from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Portal,
  Textarea,
  VStack,
} from "@chakra-ui/react"

import { Field } from "@/components/atoms/Field"
import ForeignKeySelect from "@/components/molecules/ForeignKeySelect"
import { useClassTemplates } from "@/hooks/api/useClassTemplates"
import { useSessions } from "@/hooks/api/useSessions"
import { useCurrentUser } from "@/hooks/useCurrentUser"

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

type ClassSessionFormValues = ClassSessionUpdateInput

interface ClassSessionFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<ClassSessionFormValues>
  defaultValues: ClassSessionFormValues
  onSubmit: (values: ClassSessionFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
}

const ClassSessionFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: ClassSessionFormDialogProps) => {
    const formSectionId = useId()

    // Get current user ID to filter by ownership
    const { userId } = useCurrentUser()

    // Fetch class templates for dropdown (filtered by current user)
    const { data: classTemplatesData, isLoading: classTemplatesLoading } =
      useClassTemplates({
        created_by: userId ?? undefined,
      })

    // Fetch sessions for dropdown
    const { data: sessionsData, isLoading: sessionsLoading } = useSessions({
      limit: 100,
    })

    const normalizedDefaults = useMemo<ClassSessionFormValues>(
      () => ({
        class_template_id: defaultValues.class_template_id ?? "",
        session_id: defaultValues.session_id ?? null,
        instructor_id: defaultValues.instructor_id ?? null,
        session_date: defaultValues.session_date ?? "",
        session_start: defaultValues.session_start ?? "",
        session_end: defaultValues.session_end ?? "",
        status: defaultValues.status ?? null,
        cancellation_reason: defaultValues.cancellation_reason ?? null,
        location_override: defaultValues.location_override ?? null,
        max_students_override: defaultValues.max_students_override ?? null,
        notes: defaultValues.notes ?? null,
      }),
      [defaultValues],
    )

    const form = useForm<ClassSessionFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    useEffect(() => {
      if (isOpen) {
        void form.reset(normalizedDefaults)
      }
    }, [form, isOpen, normalizedDefaults])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<ClassSessionFormValues> = async (
      values,
    ) => {
      try {
        await onSubmit(values)
        handleClose()
      } catch (error) {
        // Error handling is delegated to the mutation layer
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "md" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent as="form" onSubmit={form.handleSubmit(handleSubmit)} maxH="90vh" display="flex" flexDirection="column">
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <VStack gap={6}>
                  <Field
                    required
                    invalid={!!form.formState.errors.class_template_id}
                    errorText={form.formState.errors.class_template_id?.message}
                    label="Class Template"
                  >
                    <ForeignKeySelect
                      register={form.register("class_template_id")}
                      options={classTemplatesData?.items}
                      isLoading={classTemplatesLoading}
                      placeholder="Select Class Template"
                      hasError={!!form.formState.errors.class_template_id}
                      formatLabel={(t) =>
                        `${t.name} - ${t.swim_level} (${t.day_of_week} ${t.start_time})`
                      }
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.session_id}
                    errorText={form.formState.errors.session_id?.message}
                    label="Academic Session"
                  >
                    <ForeignKeySelect
                      register={form.register("session_id")}
                      options={sessionsData?.items}
                      isLoading={sessionsLoading}
                      placeholder="Select Academic Session (optional)"
                      hasError={!!form.formState.errors.session_id}
                      formatLabel={(s) =>
                        `${s.name} (${s.start_week} - ${s.end_week})`
                      }
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.instructor_id}
                    errorText={form.formState.errors.instructor_id?.message}
                    label="Instructor Id"
                  >
                    <Input
                      placeholder={"Enter instructor id"}
                      aria-invalid={!!form.formState.errors.instructor_id}
                      {...form.register("instructor_id")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.session_date}
                    errorText={form.formState.errors.session_date?.message}
                    label="Session Date"
                  >
                    <Input
                      type="date"
                      placeholder={"Select session date"}
                      aria-invalid={!!form.formState.errors.session_date}
                      {...form.register("session_date")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.session_start}
                    errorText={form.formState.errors.session_start?.message}
                    label="Session Start"
                  >
                    <Input
                      type="datetime-local"
                      placeholder={"Enter session start"}
                      aria-invalid={!!form.formState.errors.session_start}
                      {...form.register("session_start")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.session_end}
                    errorText={form.formState.errors.session_end?.message}
                    label="Session End"
                  >
                    <Input
                      type="datetime-local"
                      placeholder={"Enter session end"}
                      aria-invalid={!!form.formState.errors.session_end}
                      {...form.register("session_end")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.status}
                    errorText={form.formState.errors.status?.message}
                    label="Status"
                  >
                    <select
                      {...form.register("status", {
                        setValueAs: (value) => (value === "" ? null : value),
                      })}
                      aria-invalid={!!form.formState.errors.status}
                      className="chakra-select"
                    >
                      <option value="">Select Status</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="rescheduled">Rescheduled</option>
                    </select>
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.cancellation_reason}
                    errorText={
                      form.formState.errors.cancellation_reason?.message
                    }
                    label="Cancellation Reason"
                  >
                    <Textarea
                      {...form.register("cancellation_reason")}
                      placeholder={"Enter cancellation reason"}
                      rows={3}
                      resize="vertical"
                      aria-invalid={!!form.formState.errors.cancellation_reason}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.location_override}
                    errorText={form.formState.errors.location_override?.message}
                    label="Location Override"
                  >
                    <Input
                      placeholder={"Enter location override"}
                      aria-invalid={!!form.formState.errors.location_override}
                      {...form.register("location_override")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.max_students_override}
                    errorText={
                      form.formState.errors.max_students_override?.message
                    }
                    label="Max Students Override"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter max students override"}
                      aria-invalid={
                        !!form.formState.errors.max_students_override
                      }
                      {...form.register("max_students_override", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.notes}
                    errorText={form.formState.errors.notes?.message}
                    label="Notes"
                  >
                    <Textarea
                      {...form.register("notes")}
                      placeholder={"Enter notes"}
                      rows={3}
                      resize="vertical"
                      aria-invalid={!!form.formState.errors.notes}
                    />
                  </Field>
                </VStack>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

ClassSessionFormDialog.displayName = "ClassSessionFormDialog"

export default ClassSessionFormDialog
